/**
 * Client-side JWT exp claim reader.
 *
 * We do NOT verify the signature — the API does that on every request. We just
 * peek at the `exp` claim so the SPA can pre-emptively rotate a stale token
 * (e.g. after the laptop was asleep for a day) instead of letting the first
 * protected request 401 and trigger a noisy round-trip.
 */
export function readExpClaim(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True when the token is missing exp, already expired, or expires within `skewSeconds`. */
export function isStaleOrExpired(token: string, skewSeconds = 60): boolean {
  const exp = readExpClaim(token);
  if (exp === null) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp - nowSec <= skewSeconds;
}
